import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Smartphone, Mail, RefreshCcw, Power, PowerOff,
  Trash2, Plus, Link, MessageSquareCode, CheckCircle2,
  Wifi, WifiOff, Clock, AlertCircle, Send, X
} from "lucide-react";
import { getApiUrl } from "@/lib/utils";

const smtpSchema = z.object({
  host: z.string().min(1, "Host is required"),
  port: z.string().transform((val) => parseInt(val, 10)),
  user: z.string().email("Valid email required"),
  pass: z.string().min(1, "Password is required"),
});
type SmtpFormValues = z.infer<typeof smtpSchema>;

function getAuthToken() {
  const authData = localStorage.getItem("medu_auth");
  return authData ? JSON.parse(authData).token : null;
}

const TENANT_ID = import.meta.env.VITE_TENANT_ID || 'ayursangrah';

function apiHeaders(json = false) {
  const h: Record<string, string> = { 
    Authorization: `Bearer ${getAuthToken()}`,
    "x-tenant-id": TENANT_ID,
  };
  if (json) h["Content-Type"] = "application/json";
  return h;
}

const STATUS_LABELS: Record<string, { label: string; color: string; icon: any }> = {
  connected:     { label: "Connected",     color: "text-green-600",  icon: Wifi },
  qr_ready:      { label: "Scan QR",       color: "text-yellow-600", icon: Smartphone },
  uninitialized: { label: "Starting...",   color: "text-blue-500",   icon: RefreshCcw },
  disconnected:  { label: "Disconnected",  color: "text-red-500",    icon: WifiOff },
};

export default function CommunicationSettings() {
  const [status, setStatus] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [webhookUrl, setWebhookUrl] = useState("");
  const [newTrigger, setNewTrigger] = useState("");
  const [newReply, setNewReply] = useState("");
  const [newType, setNewType] = useState("exact");

  // Broadcast state variables
  const [broadcastSubject, setBroadcastSubject] = useState("");
  const [broadcastDescription, setBroadcastDescription] = useState("");
  const [broadcastRecipientType, setBroadcastRecipientType] = useState<"all" | "custom">("all");
  const [studentSearch, setStudentSearch] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<any[]>([]);
  const [isSearchingStudents, setIsSearchingStudents] = useState(false);
  const [isSendingBroadcast, setIsSendingBroadcast] = useState(false);
  const [activeTab, setActiveTab] = useState<"gateway" | "broadcast">("gateway");

  const { register, handleSubmit, formState: { errors }, reset } = useForm<SmtpFormValues>({
    resolver: zodResolver(smtpSchema),
  });

  // ---- Fetch Status ----
  const fetchStatus = async () => {
    try {
      const res = await fetch(`${getApiUrl()}/api/communication/communication-status`, {
        headers: apiHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        setStatus(data.data);
        setWebhookUrl(data.data.whatsappWebhookUrl || "");
        if (data.data.smtpConfig) {
          reset({
            host: data.data.smtpConfig.host,
            port: data.data.smtpConfig.port?.toString() || "",
            user: data.data.smtpConfig.user,
            pass: "••••••••",
          });
        }
        return data.data;
      }
    } catch (err) {
      console.error("fetchStatus error:", err);
    }
    return null;
  };

  // ---- Smart Polling ----
  // Poll every 3s when "uninitialized" or "qr_ready" (waiting for scan).
  // Stop polling when "connected" or "disconnected".
  const scheduleNextPoll = (currentStatus: string | undefined) => {
    if (pollingRef.current) clearTimeout(pollingRef.current);

    const shouldPoll = currentStatus === "uninitialized" || currentStatus === "qr_ready";
    if (!shouldPoll) return;

    pollingRef.current = setTimeout(async () => {
      const data = await fetchStatus();
      scheduleNextPoll(data?.whatsappStatus);
    }, 3000);
  };

  useEffect(() => {
    fetchStatus().then((data) => scheduleNextPoll(data?.whatsappStatus));
    return () => { if (pollingRef.current) clearTimeout(pollingRef.current); };
  }, []);

  // Debounced search for custom recipients
  useEffect(() => {
    if (!studentSearch.trim() || broadcastRecipientType !== "custom") {
      setSearchResults([]);
      return;
    }
    const delayDebounce = setTimeout(async () => {
      setIsSearchingStudents(true);
      try {
        const res = await fetch(`${getApiUrl()}/api/students?search=${encodeURIComponent(studentSearch)}&limit=10`, {
          headers: apiHeaders()
        });
        const data = await res.json();
        if (data && data.data) {
          // Filter out already selected students
          const filtered = data.data.filter(
            (student: any) => !selectedStudents.some(s => s.id === student.id)
          );
          setSearchResults(filtered);
        }
      } catch (err) {
        console.error("Search students error:", err);
      } finally {
        setIsSearchingStudents(false);
      }
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [studentSearch, selectedStudents, broadcastRecipientType]);

  const handleBroadcastSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!broadcastSubject.trim() || !broadcastDescription.trim()) {
      toast.error("Subject and description are required.");
      return;
    }
    if (broadcastRecipientType === "custom" && selectedStudents.length === 0) {
      toast.error("Please select at least one student.");
      return;
    }

    setIsSendingBroadcast(true);
    try {
      const payload = {
        subject: broadcastSubject.trim(),
        description: broadcastDescription.trim(),
        recipientType: broadcastRecipientType,
        recipients: broadcastRecipientType === "custom" ? selectedStudents.map((s) => s.id) : null,
      };

      const res = await fetch(`${getApiUrl()}/api/communication/whatsapp/send-message`, {
        method: "POST",
        headers: apiHeaders(true),
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (result.success) {
        toast.success("Broadcast started successfully in the background.");
        setBroadcastSubject("");
        setBroadcastDescription("");
        setSelectedStudents([]);
      } else {
        toast.error(result.message || "Failed to start broadcast.");
      }
    } catch (err: any) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsSendingBroadcast(false);
    }
  };

  // ---- SMTP Submit ----
  const onSmtpSubmit = async (data: SmtpFormValues) => {
    setIsSaving(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/communication/smtp`, {
        method: "PUT",
        headers: apiHeaders(true),
        body: JSON.stringify(data),
      });
      const result = await res.json();
      if (result.success) {
        toast.success("SMTP configuration saved!");
        fetchStatus();
      } else {
        toast.error(result.message);
      }
    } catch {
      toast.error("Failed to save SMTP settings");
    } finally {
      setIsSaving(false);
    }
  };

  // ---- WhatsApp Actions ----
  const initWhatsApp = async () => {
    setIsLoading(true);
    try {
      await fetch(`${getApiUrl()}/api/communication/whatsapp/init`, {
        method: "POST",
        headers: apiHeaders(),
      });
      toast.success("WhatsApp initializing… Please wait for the QR code.");
      // Immediately start polling
      const data = await fetchStatus();
      scheduleNextPoll(data?.whatsappStatus || "uninitialized");
    } catch {
      toast.error("Failed to initialize WhatsApp");
    } finally {
      setIsLoading(false);
    }
  };

  const logoutWhatsApp = async () => {
    try {
      await fetch(`${getApiUrl()}/api/communication/whatsapp/logout`, {
        method: "POST",
        headers: apiHeaders(),
      });
      toast.success("WhatsApp session disconnected");
      fetchStatus();
    } catch {
      toast.error("Failed to disconnect WhatsApp");
    }
  };

  const saveWebhook = async () => {
    try {
      const res = await fetch(`${getApiUrl()}/api/communication/whatsapp/config`, {
        method: "PUT",
        headers: apiHeaders(true),
        body: JSON.stringify({ webhookUrl }),
      });
      const result = await res.json();
      result.success
        ? toast.success("Webhook URL saved!")
        : toast.error(result.message);
    } catch {
      toast.error("Failed to save webhook URL");
    }
  };

  const addRule = async () => {
    if (!newTrigger.trim() || !newReply.trim()) {
      toast.error("Both Trigger keyword and Reply message are required!");
      return;
    }
    try {
      const res = await fetch(`${getApiUrl()}/api/communication/whatsapp/rules`, {
        method: "POST",
        headers: apiHeaders(true),
        body: JSON.stringify({ trigger: newTrigger, reply: newReply, type: newType }),
      });
      const result = await res.json();
      if (result.success) {
        toast.success("Auto-reply rule added!");
        setNewTrigger("");
        setNewReply("");
        fetchStatus();
      } else {
        toast.error(result.message);
      }
    } catch {
      toast.error("Failed to add rule");
    }
  };

  const deleteRule = async (index: number) => {
    try {
      const res = await fetch(`${getApiUrl()}/api/communication/whatsapp/rules/${index}`, {
        method: "DELETE",
        headers: apiHeaders(),
      });
      const result = await res.json();
      result.success
        ? (toast.success("Rule deleted"), fetchStatus())
        : toast.error(result.message);
    } catch {
      toast.error("Failed to delete rule");
    }
  };

  // ---- Status Indicator ----
  const wStatus = status?.whatsappStatus || "disconnected";
  const statusMeta = STATUS_LABELS[wStatus] || STATUS_LABELS.disconnected;
  const StatusIcon = statusMeta.icon;

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto space-y-8">
      {/* Header */}
      <div className="border-b pb-6">
        <h1 className="text-3xl font-bold tracking-tight">Communication Settings</h1>
        <p className="text-muted-foreground mt-1">
          Configure email (SMTP) and WhatsApp to send OTPs, updates, and automated messages to your students.
        </p>
      </div>

      {/* Premium Tabs Navigation */}
      <div className="flex gap-2 border-b pb-1">
        <button
          type="button"
          onClick={() => setActiveTab('gateway')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-all ${
            activeTab === 'gateway'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Gateway & Settings
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('broadcast')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-all ${
            activeTab === 'broadcast'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Send WhatsApp Message
        </button>
      </div>

      {activeTab === 'gateway' && (
        <>
          {/* Top Row: Email + WhatsApp */}
          <div className="grid lg:grid-cols-2 gap-6">

        {/* ── Email SMTP ── */}
        <div className="bg-card border rounded-2xl p-6 shadow-sm space-y-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-semibold text-lg">Email (SMTP)</h2>
              <p className="text-xs text-muted-foreground">
                {status?.smtpConfigured ? (
                  <span className="text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Configured</span>
                ) : (
                  <span className="text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Not Configured</span>
                )}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSmtpSubmit)} className="space-y-3">
            <div>
              <Label className="text-xs font-medium">SMTP Host</Label>
              <Input className="mt-1" placeholder="smtp.gmail.com" {...register("host")} />
              {errors.host && <p className="text-xs text-destructive mt-1">{errors.host.message}</p>}
            </div>
            <div>
              <Label className="text-xs font-medium">SMTP Port</Label>
              <Input className="mt-1" placeholder="465" {...register("port")} />
              {errors.port && <p className="text-xs text-destructive mt-1">{errors.port.message}</p>}
            </div>
            <div>
              <Label className="text-xs font-medium">Email Username</Label>
              <Input className="mt-1" placeholder="you@gmail.com" {...register("user")} />
              {errors.user && <p className="text-xs text-destructive mt-1">{errors.user.message}</p>}
            </div>
            <div>
              <Label className="text-xs font-medium">App Password</Label>
              <Input className="mt-1" type="password" placeholder="••••••••" {...register("pass")} />
              {errors.pass && <p className="text-xs text-destructive mt-1">{errors.pass.message}</p>}
            </div>
            <Button type="submit" disabled={isSaving} className="w-full mt-2">
              {isSaving ? "Saving…" : "Save SMTP Settings"}
            </Button>
          </form>
        </div>

        {/* ── WhatsApp Session ── */}
        <div className="bg-card border rounded-2xl p-6 shadow-sm flex flex-col">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2.5 bg-green-50 text-green-600 rounded-xl">
              <Smartphone className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-semibold text-lg">WhatsApp Session</h2>
              <p className={`text-xs font-medium flex items-center gap-1.5 ${statusMeta.color}`}>
                <StatusIcon className={`w-3.5 h-3.5 ${wStatus === "uninitialized" ? "animate-spin" : ""}`} />
                {statusMeta.label}
              </p>
            </div>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center border-2 border-dashed border-muted rounded-xl p-6 bg-muted/30 min-h-[220px]">
            {wStatus === "connected" && (
              <div className="text-center space-y-3">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto">
                  <Wifi className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="font-bold text-green-700">WhatsApp Connected!</h3>
                  <p className="text-xs text-muted-foreground mt-1">Ready to send OTPs and notifications.</p>
                </div>
                <Button variant="destructive" size="sm" onClick={logoutWhatsApp}>
                  <PowerOff className="w-4 h-4 mr-2" /> Unlink Session
                </Button>
              </div>
            )}

            {wStatus === "qr_ready" && status?.whatsappQrCode && (
              <div className="text-center space-y-3">
                <p className="text-sm font-semibold text-gray-700">Scan to Link WhatsApp</p>
                <div className="bg-white p-3 rounded-xl shadow-md inline-block border">
                  <img
                    src={status.whatsappQrCode}
                    alt="WhatsApp QR Code"
                    className="w-[200px] h-[200px] rounded"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Open WhatsApp → <strong>Linked Devices</strong> → <strong>Link a Device</strong>
                </p>
                <p className="text-xs text-yellow-600 flex items-center gap-1 justify-center">
                  <Clock className="w-3.5 h-3.5" /> QR expires in ~60 seconds — refresh if expired
                </p>
                <Button size="sm" variant="outline" onClick={async () => {
                  const data = await fetchStatus();
                  scheduleNextPoll(data?.whatsappStatus);
                }}>
                  <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh QR
                </Button>
              </div>
            )}

            {wStatus === "qr_ready" && !status?.whatsappQrCode && (
              <div className="text-center space-y-2">
                <RefreshCcw className="w-8 h-8 text-yellow-500 animate-spin mx-auto" />
                <p className="text-sm font-medium text-gray-600">Generating QR Code…</p>
              </div>
            )}

            {wStatus === "uninitialized" && (
              <div className="text-center space-y-2">
                <RefreshCcw className="w-8 h-8 text-primary animate-spin mx-auto" />
                <p className="text-sm font-medium text-gray-700">Starting WhatsApp Session…</p>
                <p className="text-xs text-muted-foreground">This may take up to 30 seconds</p>
              </div>
            )}

            {(wStatus === "disconnected" || !status) && (
              <div className="text-center space-y-3">
                <div className="w-14 h-14 bg-gray-100 text-gray-400 rounded-full flex items-center justify-center mx-auto">
                  <WifiOff className="w-7 h-7" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">WhatsApp Not Connected</p>
                  <p className="text-xs text-muted-foreground">Connect to send OTPs via WhatsApp</p>
                </div>
                <Button
                  onClick={initWhatsApp}
                  disabled={isLoading}
                  className="bg-[#25D366] hover:bg-[#1DA851] text-white"
                >
                  {isLoading ? (
                    <><RefreshCcw className="w-4 h-4 mr-2 animate-spin" /> Starting…</>
                  ) : (
                    <><Power className="w-4 h-4 mr-2" /> Connect WhatsApp</>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Advanced Gateway Settings (only when connected) */}
      {wStatus === "connected" && (
        <div className="bg-card border rounded-2xl p-6 shadow-sm space-y-6">
          <div className="flex items-center gap-3 border-b pb-4">
            <div className="p-2.5 bg-purple-50 text-purple-600 rounded-xl">
              <MessageSquareCode className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-semibold text-lg">Advanced WhatsApp Gateway</h2>
              <p className="text-xs text-muted-foreground">
                Set up incoming webhooks and auto-reply rules for student messages.
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Webhook */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Link className="w-4 h-4 text-purple-500" />
                <h3 className="font-medium">Incoming Message Webhook</h3>
              </div>
              <p className="text-xs text-muted-foreground">
                When a student sends a WhatsApp message, we POST to this URL with{" "}
                <code className="bg-muted px-1 rounded text-[11px]">from</code>,{" "}
                <code className="bg-muted px-1 rounded text-[11px]">message</code>, and{" "}
                <code className="bg-muted px-1 rounded text-[11px]">timestamp</code>.
              </p>
              <div className="flex gap-2">
                <Input
                  type="url"
                  placeholder="https://your-api.com/webhook"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                />
                <Button onClick={saveWebhook} variant="secondary">Save</Button>
              </div>
            </div>

            {/* Auto-Reply Rules */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <MessageSquareCode className="w-4 h-4 text-green-500" />
                <h3 className="font-medium">Auto-Reply Rules</h3>
              </div>

              <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
                {status?.whatsappRules?.length > 0 ? (
                  status.whatsappRules.map((rule: any, idx: number) => (
                    <div
                      key={idx}
                      className="flex items-start justify-between gap-2 p-3 bg-muted/50 rounded-lg border text-sm"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-green-100 text-green-800">
                            {rule.type === "contains" ? "Contains" : "Exact"}
                          </span>
                          <span className="font-mono font-semibold text-xs truncate">"{rule.trigger}"</span>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">↳ {rule.reply}</p>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="shrink-0 h-7 w-7 text-destructive hover:bg-destructive/10"
                        onClick={() => deleteRule(idx)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-muted-foreground italic py-4 text-center">
                    No auto-reply rules yet. Add one below.
                  </p>
                )}
              </div>

              <div className="border-t pt-3 space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Add New Rule</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Trigger Keyword</Label>
                    <Input
                      className="mt-1"
                      placeholder="e.g. hello"
                      value={newTrigger}
                      onChange={(e) => setNewTrigger(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Match Type</Label>
                    <select
                      className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      value={newType}
                      onChange={(e) => setNewType(e.target.value)}
                    >
                      <option value="exact">Exact Match</option>
                      <option value="contains">Contains Text</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Reply Message</Label>
                  <textarea
                    className="mt-1 flex min-h-[70px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    placeholder="Type the auto-reply message…"
                    value={newReply}
                    onChange={(e) => setNewReply(e.target.value)}
                  />
                </div>
                <Button onClick={addRule} className="w-full bg-green-600 hover:bg-green-700 text-white" size="sm">
                  <Plus className="w-4 h-4 mr-2" /> Add Auto-Reply Rule
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {activeTab === 'broadcast' && (
        <div className="space-y-6">
          {wStatus !== "connected" && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl p-5 flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-sm">WhatsApp Connection Required</h4>
                <p className="text-xs text-amber-700 mt-1">
                  You must connect a WhatsApp session before sending broadcast messages. Please navigate to the <strong>Gateway & Settings</strong> tab to scan the QR code.
                </p>
              </div>
            </div>
          )}

          <div className="bg-card border rounded-2xl p-6 shadow-sm space-y-6">
            <div className="flex items-center gap-3 border-b pb-4">
              <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl">
                <Send className="w-5 h-5" />
              </div>
              <div>
                <h2 className="font-semibold text-lg">Send WhatsApp Broadcast</h2>
                <p className="text-xs text-muted-foreground">
                  Send a bulk text message to all active students or select individual recipients.
                </p>
              </div>
            </div>

            <form onSubmit={handleBroadcastSubmit} className="space-y-5">
              {/* Message Subject */}
              <div className="space-y-1">
                <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Message Subject
                </Label>
                <Input
                  placeholder="e.g. IMPORTANT: Class Schedule Change"
                  value={broadcastSubject}
                  onChange={(e) => setBroadcastSubject(e.target.value)}
                  disabled={wStatus !== "connected" || isSendingBroadcast}
                  className="mt-1"
                />
                <p className="text-[11px] text-muted-foreground">
                  This will be shown at the very beginning of the WhatsApp message in *bold*.
                </p>
              </div>

              {/* Message Description */}
              <div className="space-y-1">
                <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Message Description
                </Label>
                <textarea
                  className="mt-1 flex min-h-[140px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                  placeholder="Type your announcement here…"
                  value={broadcastDescription}
                  onChange={(e) => setBroadcastDescription(e.target.value)}
                  disabled={wStatus !== "connected" || isSendingBroadcast}
                />
                <div className="flex justify-between items-center text-[11px] text-muted-foreground">
                  <span>Formatting: *bold*, _italic_, ~strike~, ```code```</span>
                  <span>{broadcastDescription.length} characters</span>
                </div>
              </div>

              {/* Recipient Selection */}
              <div className="space-y-3">
                <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Recipients
                </Label>
                <div className="flex flex-wrap gap-4 mt-1">
                  <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                    <input
                      type="radio"
                      name="recipientType"
                      value="all"
                      checked={broadcastRecipientType === "all"}
                      onChange={() => setBroadcastRecipientType("all")}
                      disabled={wStatus !== "connected" || isSendingBroadcast}
                      className="w-4 h-4 text-primary"
                    />
                    <span>All Active Students</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                    <input
                      type="radio"
                      name="recipientType"
                      value="custom"
                      checked={broadcastRecipientType === "custom"}
                      onChange={() => setBroadcastRecipientType("custom")}
                      disabled={wStatus !== "connected" || isSendingBroadcast}
                      className="w-4 h-4 text-primary"
                    />
                    <span>Specific Chosen Students</span>
                  </label>
                </div>
              </div>

              {/* Student Picker (for custom type) */}
              {broadcastRecipientType === "custom" && (
                <div className="space-y-3 border-t pt-4">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-muted-foreground">
                      Search Students
                    </Label>
                    <div className="relative">
                      <Input
                        placeholder="Search student by name, email, or mobile..."
                        value={studentSearch}
                        onChange={(e) => setStudentSearch(e.target.value)}
                        disabled={wStatus !== "connected" || isSendingBroadcast}
                        className="pr-10"
                      />
                      {isSearchingStudents && (
                        <div className="absolute right-3 top-2.5">
                          <RefreshCcw className="w-4 h-4 animate-spin text-muted-foreground" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Search Results list */}
                  {searchResults.length > 0 && (
                    <div className="border rounded-lg bg-background shadow-md max-h-[220px] overflow-y-auto divide-y">
                      {searchResults.map((student: any) => (
                        <div
                          key={student.id}
                          onClick={() => {
                            setSelectedStudents([...selectedStudents, student]);
                            setStudentSearch("");
                            setSearchResults([]);
                          }}
                          className="p-3 hover:bg-muted/80 cursor-pointer flex justify-between items-center text-sm transition-colors"
                        >
                          <div>
                            <p className="font-semibold text-foreground">{student.name}</p>
                            <p className="text-xs text-muted-foreground">{student.email}</p>
                          </div>
                          <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-900">
                            {student.mobile || "No Mobile"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Selected Students badges */}
                  {selectedStudents.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground">
                        Selected Recipients ({selectedStudents.length})
                      </Label>
                      <div className="flex flex-wrap gap-2 max-h-[200px] overflow-y-auto p-2 border rounded-lg bg-muted/20">
                        {selectedStudents.map((student: any) => (
                          <div
                            key={student.id}
                            className="flex items-center gap-1.5 pl-3 pr-1.5 py-1 bg-background border rounded-full text-xs font-medium shadow-sm"
                          >
                            <span className="text-foreground">{student.name}</span>
                            <span className="text-muted-foreground">({student.mobile || "No Mobile"})</span>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedStudents(selectedStudents.filter((s) => s.id !== student.id));
                              }}
                              className="w-5 h-5 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={wStatus !== "connected" || isSendingBroadcast || !broadcastSubject.trim() || !broadcastDescription.trim() || (broadcastRecipientType === "custom" && selectedStudents.length === 0)}
                className="w-full bg-[#25D366] hover:bg-[#1DA851] text-white flex items-center justify-center gap-2 py-6 rounded-xl font-semibold shadow-md transition-all active:scale-[0.98] disabled:opacity-50"
              >
                {isSendingBroadcast ? (
                  <><RefreshCcw className="w-5 h-5 animate-spin" /> Starting Broadcast...</>
                ) : (
                  <><Send className="w-4 h-4" /> Send WhatsApp Broadcast</>
                )}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
